import React, { useState, useEffect, useRef } from 'react';
import Icon from './Icon';

const VoiceInput = ({ onTranscript, disabled = false, placeholder = "Klicka på mikrofonen för att börja tala..." }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const manualStopRef = useRef(false); // Track if we manually stopped
  const isListeningRef = useRef(false); // Track listening state for event handlers
  const isStartingRef = useRef(false); // Track if we're in the process of starting

  useEffect(() => {
    // Check if Web Speech API is supported
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setIsSupported(true);
      
      // Initialize speech recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      // Configure recognition settings
      recognition.continuous = false; // Go back to false for better control
      recognition.interimResults = true;
      recognition.lang = 'sv-SE'; // Swedish
      recognition.maxAlternatives = 1;

      // Handle results
      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptPart = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcriptPart;
          } else {
            interimTranscript += transcriptPart;
          }
        }

        const fullTranscript = finalTranscript || interimTranscript;
        setTranscript(fullTranscript);
        
        if (finalTranscript) {
          onTranscript(finalTranscript);
          setTranscript(''); // Clear the preview after sending
        }
      };

      // Handle errors
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        // Don't show errors for aborted (happens during normal stopping)
        if (event.error !== 'aborted') {
          setError(`Fel vid taligenkänning: ${event.error}`);
        }
        
        isStartingRef.current = false;
        
        // Only stop listening if it's a real error, not abort or no-speech
        if (event.error !== 'aborted' && event.error !== 'no-speech') {
          setIsListening(false);
          isListeningRef.current = false;
        }
      };

      // Handle end of recognition
      recognition.onend = () => {
        console.log('🎤 Speech recognition ended, manualStop:', manualStopRef.current, 'isListening:', isListeningRef.current);
        isStartingRef.current = false;
        
        // If manually stopped, clean up and don't restart
        if (manualStopRef.current) {
          console.log('🎤 Manual stop detected, cleaning up');
          setIsListening(false);
          isListeningRef.current = false;
          manualStopRef.current = false; // Reset flag
          return;
        }
        
        // Auto-restart if still supposed to be listening
        if (isListeningRef.current && !isStartingRef.current) {
          setTimeout(() => {
            if (isListeningRef.current && !manualStopRef.current && !isStartingRef.current) {
              try {
                console.log('🎤 Auto-restarting recognition after end');
                isStartingRef.current = true;
                recognitionRef.current.start();
              } catch (error) {
                console.log('Error auto-restarting:', error);
                isStartingRef.current = false;
                setIsListening(false);
                isListeningRef.current = false;
              }
            }
          }, 500); // Longer delay to avoid conflicts
        } else {
          setIsListening(false);
          isListeningRef.current = false;
        }
      };

      recognitionRef.current = recognition;
    } else {
      setIsSupported(false);
      setError('Taligenkänning stöds inte i denna webbläsare. Prova Chrome eller Edge.');
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onTranscript]); // Remove isListening dependency

  const startListening = () => {
    if (!isSupported || !recognitionRef.current || disabled || isStartingRef.current) return;
    
    setError('');
    setTranscript('');
    manualStopRef.current = false; // Reset manual stop flag
    isStartingRef.current = true; // Mark as starting
    setIsListening(true);
    isListeningRef.current = true; // Update ref
    
    try {
      console.log('🎤 Starting speech recognition');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setError('Kunde inte starta taligenkänning');
      setIsListening(false);
      isListeningRef.current = false;
      isStartingRef.current = false;
    }
  };

  const stopListening = () => {
    console.log('🎤 Stopping speech recognition manually');
    
    // Immediately set all flags to stop everything
    manualStopRef.current = true;
    isListeningRef.current = false;
    isStartingRef.current = false;
    setIsListening(false);
    setTranscript('');
    
    if (recognitionRef.current) {
      try {
        console.log('🎤 Calling recognition.stop()');
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Force cleanup after a short delay
    setTimeout(() => {
      setIsListening(false);
      isListeningRef.current = false;
      isStartingRef.current = false;
      console.log('🎤 Forced cleanup completed');
    }, 300);
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  if (!isSupported) {
    return (
      <div className="flex items-center justify-center p-3 bg-gray-100 border border-gray-300 rounded-md">
        <Icon name="mic-off" size={16} className="text-gray-400 mr-2" />
        <span className="text-sm text-gray-500">Taligenkänning stöds ej</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleListening}
          disabled={disabled}
          className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-200 ${
            isListening
              ? 'bg-red-500 border-red-500 text-white animate-pulse shadow-lg'
              : disabled
              ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
              : 'bg-white border-blue-500 text-blue-500 hover:bg-blue-500 hover:border-blue-500 hover:text-white hover:shadow-md'
          }`}
          title={isListening ? 'Stoppa inspelning' : 'Starta taligenkänning'}
        >
          <Icon 
            name={isListening ? 'pause' : 'mic'} 
            size={20}
            className={isListening ? 'animate-pulse' : ''}
          />
        </button>
        
        <div className="flex-1">
          {isListening ? (
            <div className="flex items-center gap-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
              <span className="text-sm text-red-600 font-medium">Lyssnar... Tala nu</span>
            </div>
          ) : (
            <span className="text-sm text-gray-500">{placeholder}</span>
          )}
        </div>
      </div>
      
      {transcript && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <Icon name="volume-2" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-blue-800 font-medium mb-1">Transkriberad text:</p>
              <p className="text-sm text-blue-700 italic">"{transcript}"</p>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <Icon name="alert-triangle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      {isListening && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded-md p-2">
          <strong>Tips:</strong> Tala tydligt och pausa mellan meningar. Inspelningen startar om automatiskt efter varje mening.
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
